import React, { useEffect, useState } from "react";
import { JhPopupDrawer } from "designPackages/popupPackage";
import { JhText } from "designPackages/textPackage";
import { JhButton } from "designPackages/buttonPackage";
import useModalStore from "zustandStores/useModalStore";
import { JhInput } from "designPackages/inputPackage";
import { JhUseToast } from "designPackages/toastPackage";
import { useForm } from "react-hook-form";
import LogService from "services/LogService";
import InvoiceService from "services/InvoiceService";
import CREDIT_EVENTS from "utils/CreditEvents";
import { modalStateValues } from "services/constants";
import { EMAIL_REGEX } from "services/regex";

interface InvoiceEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EditEmailContent = ({ setShowEmailEditSuccess, setShowEditEmail }) => {
  const { toast } = JhUseToast();
  const { modalData } = useModalStore(state => ({
    modalData: state.modalData
  }));

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors }
  } = useForm();

  const watchedEmail = watch("email");
  const isFormValid = watchedEmail && !errors.email;

  const [isLoading, setIsLoading] = useState(false);

  const handleEmailUpdate = async (data: { email: string } = { email: "" }) => {
    const emailToUpdate = data.email.trim();
    const { orderId = "" } = modalData?.data || {};

    if (!orderId || !emailToUpdate) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    }

    setIsLoading(true);
    try {
      LogService.log(CREDIT_EVENTS.GST_EMAIL_EDIT_CLICKED, {
        newEmail: emailToUpdate
      });

      await InvoiceService.emailInvoice({
        orderId: orderId.toString(),
        email: emailToUpdate
      });
      setShowEmailEditSuccess(true);
      LogService.log(CREDIT_EVENTS.INVOICE_EMAIL_UPDATE_SUCCESS, {
        newEmail: emailToUpdate
      });
    } catch (error: any) {
      console.error("Failed to send invoice:", error);
      toast({
        title: "Error",
        description: "Failed to send invoice. Please try again.",
        variant: "destructive"
      });

      LogService.log(CREDIT_EVENTS.GST_EMAIL_UPDATE_ERROR, {
        error: error?.message || error
      });
    } finally {
      setIsLoading(false);
      setShowEditEmail(false);
    }
  };

  return (
    <div className="w-full flex flex-col items-center gap-6 tw-root">
      <div className="flex flex-col items-center text-center gap-1">
        <JhText variant="emphasis2" color="foreground">
          Edit email
        </JhText>
        <JhText variant="body2" color="muted-foreground">
          Your requested invoice will be sent to this email
        </JhText>
      </div>

      <form
        onSubmit={handleSubmit(handleEmailUpdate)}
        className="w-full space-y-6"
      >
        <JhInput
          {...register("email", {
            required: "Email is required",
            pattern: {
              value: EMAIL_REGEX,
              message: "Please enter a valid email address"
            }
          })}
          type="email"
          placeholder="New email address"
          hasError={!!errors.email}
          errorMessage={
            typeof errors.email?.message === "string"
              ? errors.email.message
              : ""
          }
          disabled={isLoading}
          className="w-full"
        />

        <JhButton
          type="submit"
          disabled={!isFormValid || isLoading}
          className="w-full"
        >
          Confirm
        </JhButton>
      </form>
    </div>
  );
};

const InvoiceEmailModal: React.FC<InvoiceEmailModalProps> = ({
  isOpen,
  onClose
}) => {
  const { modalData, setModalData } = useModalStore(state => ({
    modalData: state.modalData,
    setModalData: state.setModalData
  }));

  const [showEditEmail, setShowEditEmail] = useState(false);
  const [showEmailEditSuccess, setShowEmailEditSuccess] = useState(false);
  const [isFirstInvoiceSent, setIsFirstInvoiceSent] = useState(true);

  const EmailEditSuccessContent = () => {
    return (
      <div className="flex flex-col items-center justify-center gap-1.5 p-2 mt-6 tw-root">
        <img
          src="/hire/sticker-tick.png"
          alt="success"
          width={60}
          height={60}
        />

        <JhText variant="emphasis4" color="foreground">
          Your requested invoice will be sent to this email
        </JhText>
      </div>
    );
  };

  const handleEditEmailClick = () => {
    setShowEditEmail(true);
    setIsFirstInvoiceSent(false);
    LogService.log(CREDIT_EVENTS.INVOICE_EMAIL_EDIT_CLICKED, {
      orderId: modalData?.data?.orderId,
      invoiceEmail: modalData?.data?.invoiceEmail
    });
  };

  useEffect(() => {
    if (showEmailEditSuccess) {
      const timer = setTimeout(() => {
        setShowEmailEditSuccess(false);
        setModalData({
          state: modalStateValues.CLOSE,
          type: ""
        });
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showEmailEditSuccess, setModalData]);

  return (
    <JhPopupDrawer open={isOpen} onClose={onClose} position="center">
      {isFirstInvoiceSent ? (
        <div
          id="invoice-success-modal-container"
          className="flex flex-col items-center gap-9 w-full tw-root"
        >
          <img
            id="invoice-success-modal-sticker-icon"
            src="/hire/sticker-tick.png"
            alt="Success sticker"
            height={60}
            width={60}
            className="object-cover"
          />

          <div
            id="invoice-success-modal-text-section"
            className="flex flex-col items-center gap-1"
          >
            <JhText
              id="invoice-success-modal-heading"
              variant="emphasis3"
              color="foreground"
            >
              Invoice sent to below email
            </JhText>

            <div
              id="invoice-success-modal-email-section"
              className="flex items-center gap-1"
            >
              <JhText
                id="invoice-success-modal-email-text"
                variant="emphasis4"
                color="muted-foreground"
                className="truncate"
              >
                {modalData?.data?.invoiceEmail}
              </JhText>
              <JhText
                id="invoice-success-modal-edit-button"
                variant="subtitle4"
                onClick={handleEditEmailClick}
                color="primary"
              >
                Edit
              </JhText>
            </div>
          </div>
        </div>
      ) : showEditEmail ? (
        <EditEmailContent
          setShowEmailEditSuccess={setShowEmailEditSuccess}
          setShowEditEmail={setShowEditEmail}
        />
      ) : showEmailEditSuccess ? (
        <EmailEditSuccessContent />
      ) : null}
    </JhPopupDrawer>
  );
};

export default InvoiceEmailModal;
